import React, { useEffect, useState } from "react";
import axios from "axios";
import { Check, Sparkles, Percent } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Services() {
  const [services, setServices] = useState([]);

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API}/services`)
      .then((res) => {
        if (!cancelled) setServices(res.data.services || []);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <section id="services" data-testid="services-section" className="relative px-6 md:px-12 lg:px-20 py-28 md:py-40 bg-[#0A0A0A]">
      <div className="max-w-7xl mx-auto">
        <div className="grid md:grid-cols-12 gap-10 mb-16">
          <div className="md:col-span-5">
            <div className="overline mb-5">— Packages</div>
            <h2 className="font-display text-4xl sm:text-5xl font-light tracking-tight leading-[1.05]">
              Crafted to match
              <br />
              <span className="text-white/55">every car, every owner.</span>
            </h2>
          </div>
          <div className="md:col-span-6 md:col-start-7 flex flex-col justify-end gap-5">
            <p className="text-white/65 leading-relaxed max-w-md">
              Choose the level of care that fits your vehicle. Every package is delivered by hand with
              premium products — no expressways, no shortcuts.
            </p>
            <div
              data-testid="first-wash-offer-strip"
              className="inline-flex items-center gap-3 border border-emerald-300/30 bg-emerald-300/5 px-4 py-3 self-start"
            >
              <Percent size={14} className="text-emerald-300" />
              <span className="text-xs tracking-[0.18em] uppercase text-emerald-200">
                50% off your first wash — every package
              </span>
            </div>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10 border border-white/10">
          {services.map((s, idx) => (
            <article
              key={s.id}
              data-testid={`service-card-${s.id}`}
              className={`relative bg-[#0E0E0E] hover:bg-[#141414] transition-colors duration-300 p-8 lg:p-10 flex flex-col ${
                s.featured ? "lg:row-span-1" : ""
              }`}
            >
              {s.featured && (
                <span className="absolute top-6 right-6 inline-flex items-center gap-1.5 text-[10px] tracking-[0.2em] uppercase text-black bg-white px-2.5 py-1">
                  <Sparkles size={11} /> Popular
                </span>
              )}
              <div className="overline mb-6">0{idx + 1}</div>
              <h3 className="font-display text-2xl font-medium mb-2">{s.name}</h3>
              <p className="text-sm text-white/55 mb-8">{s.tagline}</p>
              <div className="flex items-baseline gap-3 mb-1">
                <span className="font-display text-4xl font-light">£{s.price}</span>
                <span className="text-xs text-white/50">from</span>
              </div>
              <div className="flex items-baseline gap-2 mb-2" data-testid={`service-first-wash-${s.id}`}>
                <span className="text-sm text-emerald-300 font-medium">£{s.price / 2}</span>
                <span className="text-[10px] tracking-[0.18em] uppercase text-emerald-300/70">first wash · 50% off</span>
              </div>
              <div className="text-xs text-white/45 mb-8 tracking-wide">{s.duration}</div>

              <ul className="space-y-2.5 mb-10 flex-1">
                {s.inclusions?.map((inc) => (
                  <li key={inc} className="flex items-start gap-2.5 text-sm text-white/75 leading-snug">
                    <Check size={14} className="mt-1 text-white/50 flex-shrink-0" />
                    <span>{inc}</span>
                  </li>
                ))}
              </ul>

              <a
                href="#book"
                data-testid={`service-book-${s.id}`}
                className="mt-auto inline-flex items-center justify-between border-t border-white/15 pt-5 text-xs tracking-[0.2em] uppercase text-white/80 hover:text-white transition-colors"
              >
                Book this package
                <span aria-hidden>→</span>
              </a>
            </article>
          ))}
        </div>

        <p className="mt-10 text-xs text-white/40 tracking-wide">
          * Prices from. Larger vehicles (SUV, 7-seater, van) +£15. Minimum booking £60.
          First-wash discount applies to your package price for new customers — add-ons full price.
        </p>
      </div>
    </section>
  );
}
